import { useUserStore } from '@/config/store/userStore'
import { isModuleActive } from '@/services/application'

const moduleId = 8

type SalesFunnelRights = {
  isAccess?: boolean
  showClients?: boolean
  showAll?: boolean
  showStats?: boolean
  showContacts?: boolean
  showActivities?: boolean
  editPipeline?: boolean
  manageWorkflows?: boolean
  viewInsightsAdvanced?: boolean
  askCrm?: boolean
  useAgents?: boolean
  viewRelationshipInsights?: boolean
  [key: string]: unknown
}

const getSalesFunnel = (): SalesFunnelRights => {
  const userStore = useUserStore()
  return userStore.user?.profile?.modules?.salesFunnel || {}
}

/** Missing flag → treat as true when isAccess (backward compat during rollout). */
export const hasSalesFunnelFlag = (
  flag: keyof SalesFunnelRights,
  salesFunnel: SalesFunnelRights = getSalesFunnel(),
) => {
  const value = salesFunnel[flag]
  if (value === undefined)
    return !!salesFunnel.isAccess
  return !!value
}

const canUserAccessModule = (user: { firm: unknown; profile: { modules: { salesFunnel: SalesFunnelRights } } }) => {
  const isFirmAccess = isModuleActive(user.firm, moduleId)
  return isFirmAccess && user.profile.modules.salesFunnel.isAccess
}

export const guardSalesFunnelAccess = () => {
  const userStore = useUserStore()
  const user = userStore.user
  const canAccess = canUserAccessModule(user)
  return canAccess || { name: 'Home' }
}

export const guardClients = () => {
  const userStore = useUserStore()
  const user = userStore.user
  return user.profile.modules.salesFunnel.showClients || user.profile.modules.salesFunnel.showAll
    ? user.profile.modules.salesFunnel.showClients || user.profile.modules.salesFunnel.showAll
    : { name: 'Home' }
}

export const guardStats = () => {
  const userStore = useUserStore()
  const user = userStore.user
  return user.profile.modules.salesFunnel.showStats
    ? user.profile.modules.salesFunnel.showStats
    : { name: 'Home' }
}

export const guardContacts = () => {
  const salesFunnel = getSalesFunnel()
  return hasSalesFunnelFlag('showContacts', salesFunnel) || { name: 'Home' }
}

export const guardActivities = () => {
  const salesFunnel = getSalesFunnel()
  return hasSalesFunnelFlag('showActivities', salesFunnel) || { name: 'Home' }
}

export const guardWorkflows = () => {
  const salesFunnel = getSalesFunnel()
  return hasSalesFunnelFlag('manageWorkflows', salesFunnel) || { name: 'Home' }
}

export const canEditPipeline = () => {
  return hasSalesFunnelFlag('editPipeline')
}

export const canViewInsightsAdvanced = () => {
  const salesFunnel = getSalesFunnel()
  if (salesFunnel.viewInsightsAdvanced !== undefined)
    return !!salesFunnel.viewInsightsAdvanced
  return !!(salesFunnel.showAll || salesFunnel.isAccess)
}

export const canAskCrm = () => {
  const salesFunnel = getSalesFunnel()
  if (!salesFunnel.isAccess) return false
  if (salesFunnel.askCrm !== undefined) return !!salesFunnel.askCrm
  return !!salesFunnel.showAll
}

export const canUseSalesAgents = () => {
  const salesFunnel = getSalesFunnel()
  if (!salesFunnel.isAccess) return false
  if (salesFunnel.useAgents !== undefined) return !!salesFunnel.useAgents
  return !!salesFunnel.showAll
}

export const canViewRelationshipInsights = () => {
  const salesFunnel = getSalesFunnel()
  if (!salesFunnel.isAccess) return false
  if (salesFunnel.viewRelationshipInsights !== undefined) return !!salesFunnel.viewRelationshipInsights
  return !!salesFunnel.showAll
}

